import React from 'react';

/**
 * WaveBackground — lightweight animated SVG background.
 * 
 * Performance notes:
 * - Reduced from 36 threads (72 animated elements) to 8 threads (16 elements).
 * - Removed all feTurbulence / feComposite filters (most expensive SVG ops).
 * - Kept simple feGaussianBlur for neon glow only.
 * - Using CSS containment for better compositing.
 */
export default function WaveBackground() {
  return (
    <div
      className="absolute inset-0 z-0 overflow-hidden pointer-events-none opacity-60 mix-blend-screen"
      style={{ contain: 'strict' }}
    >
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 1200 800"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <radialGradient id="neonPulse1" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(255,255,255,1)" />
            <stop offset="30%" stopColor="rgba(163,230,53,1)" />
            <stop offset="70%" stopColor="rgba(132,204,22,0.8)" />
            <stop offset="100%" stopColor="rgba(132,204,22,0)" />
          </radialGradient>
          <radialGradient id="neonPulse2" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.9)" />
            <stop offset="25%" stopColor="rgba(163,230,53,0.9)" />
            <stop offset="60%" stopColor="rgba(101,163,13,0.7)" />
            <stop offset="100%" stopColor="rgba(101,163,13,0)" />
          </radialGradient>
          <radialGradient id="heroTextBg" cx="30%" cy="50%" r="70%">
            <stop offset="0%" stopColor="rgba(132,204,22,0.15)" />
            <stop offset="40%" stopColor="rgba(163,230,53,0.08)" />
            <stop offset="80%" stopColor="rgba(101,163,13,0.05)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0)" />
          </radialGradient>
          <linearGradient id="threadFade1" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(0,0,0,1)" />
            <stop offset="15%" stopColor="rgba(132,204,22,0.8)" />
            <stop offset="85%" stopColor="rgba(132,204,22,0.8)" />
            <stop offset="100%" stopColor="rgba(0,0,0,1)" />
          </linearGradient>
          <linearGradient id="threadFade2" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(0,0,0,1)" />
            <stop offset="12%" stopColor="rgba(163,230,53,0.7)" />
            <stop offset="88%" stopColor="rgba(163,230,53,0.7)" />
            <stop offset="100%" stopColor="rgba(0,0,0,1)" />
          </linearGradient>
          <linearGradient id="threadFade3" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(0,0,0,1)" />
            <stop offset="18%" stopColor="rgba(101,163,13,0.8)" />
            <stop offset="82%" stopColor="rgba(101,163,13,0.8)" />
            <stop offset="100%" stopColor="rgba(0,0,0,1)" />
          </linearGradient>
          {/* Simple glow filter — no feTurbulence */}
          <filter id="neonGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <g>
          {/* Soft ambient glow behind hero text */}
          <ellipse cx="300" cy="350" rx="400" ry="200" fill="url(#heroTextBg)" opacity="0.6" />
          <ellipse cx="400" cy="300" rx="600" ry="300" fill="url(#heroTextBg)" opacity="0.2" />

          {/* Thread 1 — Main S-curve */}
          <path
            id="thread1"
            d="M50 720 Q200 590 350 540 Q500 490 650 520 Q800 550 950 460 Q1100 370 1200 340"
            stroke="url(#threadFade1)"
            strokeWidth="0.8"
            fill="none"
            opacity="0.8"
          />
          <circle r="2" fill="url(#neonPulse1)" opacity="1">
            <animateMotion dur="4s" repeatCount="indefinite">
              <mpath href="#thread1" />
            </animateMotion>
          </circle>

          {/* Thread 2 — Gentle wave */}
          <path
            id="thread2"
            d="M80 730 Q250 620 400 570 Q550 520 700 550 Q850 580 1000 490 Q1150 400 1300 370"
            stroke="url(#threadFade2)"
            strokeWidth="1.5"
            fill="none"
            opacity="0.7"
          />
          <circle r="3" fill="url(#neonPulse2)" opacity="1">
            <animateMotion dur="5s" repeatCount="indefinite">
              <mpath href="#thread2" />
            </animateMotion>
          </circle>

          {/* Thread 3 — Organic curve */}
          <path
            id="thread3"
            d="M20 710 Q180 580 320 530 Q460 480 600 510 Q740 540 880 450 Q1020 360 1200 330"
            stroke="url(#threadFade3)"
            strokeWidth="1.2"
            fill="none"
            opacity="0.8"
          />
          <circle r="2.5" fill="url(#neonPulse1)" opacity="1">
            <animateMotion dur="4.5s" repeatCount="indefinite">
              <mpath href="#thread3" />
            </animateMotion>
          </circle>

          {/* Thread 4 — Thick prominent */}
          <path
            id="thread4"
            d="M120 740 Q280 640 450 590 Q620 540 770 570 Q920 600 1070 510 Q1220 420 1350 390"
            stroke="url(#threadFade1)"
            strokeWidth="1.5"
            fill="none"
            opacity="0.9"
          />
          <circle r="3.2" fill="url(#neonPulse2)" opacity="1">
            <animateMotion dur="5.5s" repeatCount="indefinite">
              <mpath href="#thread4" />
            </animateMotion>
          </circle>

          {/* Thread 5 — Natural wave */}
          <path
            id="thread5"
            d="M60 725 Q220 600 380 550 Q540 500 680 530 Q820 560 960 470 Q1100 380 1280 350"
            stroke="url(#threadFade2)"
            strokeWidth="1.0"
            fill="none"
            opacity="0.7"
          />
          <circle r="2.2" fill="url(#neonPulse1)" opacity="1">
            <animateMotion dur="4.2s" repeatCount="indefinite">
              <mpath href="#thread5" />
            </animateMotion>
          </circle>

          {/* Thread 6 — Thin delicate */}
          <path
            id="thread6"
            d="M150 735 Q300 660 480 610 Q660 560 800 590 Q940 620 1080 530 Q1220 440 1400 410"
            stroke="url(#threadFade3)"
            strokeWidth="0.5"
            fill="none"
            opacity="0.5"
          />
          <circle r="1.5" fill="url(#neonPulse2)" opacity="1">
            <animateMotion dur="6s" repeatCount="indefinite">
              <mpath href="#thread6" />
            </animateMotion>
          </circle>

          {/* Thread 7 — Lower wave */}
          <path
            id="thread7"
            d="M50 720 Q190 745 340 705 Q490 665 630 685 Q770 705 910 645 Q1050 585 1200 340"
            stroke="url(#threadFade1)"
            strokeWidth="1.1"
            fill="none"
            opacity="0.7"
          />
          <circle r="2.5" fill="url(#neonPulse1)" opacity="1">
            <animateMotion dur="4.8s" repeatCount="indefinite">
              <mpath href="#thread7" />
            </animateMotion>
          </circle>

          {/* Thread 8 — Final accent */}
          <path
            id="thread8"
            d="M50 720 Q215 765 375 725 Q535 685 675 705 Q815 725 955 665 Q1095 605 1200 340"
            stroke="url(#threadFade3)"
            strokeWidth="1.2"
            fill="none"
            opacity="0.8"
          />
          <circle r="2.7" fill="url(#neonPulse2)" opacity="1">
            <animateMotion dur="4.5s" repeatCount="indefinite">
              <mpath href="#thread8" />
            </animateMotion>
          </circle>
        </g>
      </svg>
    </div>
  );
}
